import {useCallback, useEffect, useRef, useState} from 'react'
import {useRouter} from 'next/router'
import {getAllPetNames} from '@/services/pet-names/client'

interface NameFilterHookResult {
	filterPetNames: (letterFrom: string, letterTo: string) => void
	filterBySpecie: (specie: string) => void
	loadMoreNames: (trait: string) => void
	loading: boolean
	loadMore: boolean
	showLoadMoreText: boolean
	selected: string
	names: string[]
	displayAllNames: () => void
}

interface PetNameResponse {
	names: string[]
}

type ApiResponse = (string | PetNameResponse)[]

export function useNameFilter(): NameFilterHookResult {
	const {locale = ''} = useRouter()
	const namesLocaleRef = useRef<string | null>(null)
	const requestGenerationRef = useRef(0)
	const [petResultsList, setPetResultsList] = useState<string[]>([])
	const [loading, setLoading] = useState(false)
	const [loadMore, setLoadMore] = useState(false)
	const [showLoadMoreText, setShowLoadMoreText] = useState(true)
	const [selected, setSelected] = useState<string>()
	const [selectedLetters, setSelectedLetters] = useState(['A', 'B', 'C', 'D'])

	const fetchAllPetNames = useCallback(async () => {
		if (namesLocaleRef.current === locale) {
			requestGenerationRef.current += 1
			setLoading(false)
			return
		}

		const generation = ++requestGenerationRef.current
		setLoading(true)
		namesLocaleRef.current = null
		setPetResultsList([])

		try {
			const response = await getAllPetNames({locale})
			if (generation !== requestGenerationRef.current) {
				return
			}
			namesLocaleRef.current = locale
			setPetResultsList(response)
		} catch {
			if (generation !== requestGenerationRef.current) {
				return
			}
			namesLocaleRef.current = null
			setPetResultsList([])
		} finally {
			if (generation === requestGenerationRef.current) {
				setLoading(false)
			}
		}
	}, [locale])

	async function fetchSpeciesName(specie: string): Promise<void> {
		if (petResultsList.length > 0 && namesLocaleRef.current === locale) {
			requestGenerationRef.current += 1
			setLoading(false)
			return
		}

		const generation = ++requestGenerationRef.current
		setLoading(true)

		try {
			const response = (await getAllPetNames({
				type: specie,
				locale,
			})) as ApiResponse

			if (generation !== requestGenerationRef.current) {
				return
			}

			namesLocaleRef.current = locale
			setPetResultsList(response as string[])
		} catch {
			if (generation !== requestGenerationRef.current) {
				return
			}
			namesLocaleRef.current = null
			setPetResultsList([])
		} finally {
			if (generation === requestGenerationRef.current) {
				setLoading(false)
			}
		}
	}

	useEffect(() => {
		void fetchAllPetNames()
	}, [fetchAllPetNames])

	const filterPetNames = (letterFrom: string, letterTo: string): void => {
		setLoadMore(false)
		setShowLoadMoreText(true)
		const alphabet = [
			'A',
			'B',
			'C',
			'D',
			'E',
			'F',
			'G',
			'H',
			'I',
			'J',
			'K',
			'L',
			'M',
			'N',
			'O',
			'P',
			'Q',
			'R',
			'S',
			'T',
			'U',
			'V',
			'W',
			'X',
			'Y',
			'Z',
		]
		const indexOfFrom = alphabet.indexOf(letterFrom)
		const indexOfTo = alphabet.indexOf(letterTo)
		setSelectedLetters(alphabet.slice(indexOfFrom, indexOfTo + 1))
	}

	const filterBySpecie = (specie: string): void => {
		setSelected(specie)
		void fetchSpeciesName(specie)
	}

	const loadMoreNames = (trait: string): void => {
		setSelected(trait)
		void fetchAllPetNames()
	}

	const displayAllNames = (): void => {
		setLoadMore(true)
		setShowLoadMoreText(false)
	}

	const filteredNames = petResultsList.filter((name) =>
		selectedLetters.includes(name.charAt(0).toUpperCase())
	)

	return {
		filterPetNames,
		filterBySpecie,
		loadMoreNames,
		loading,
		loadMore,
		showLoadMoreText,
		selected: selected ?? '',
		names: filteredNames,
		displayAllNames,
	}
}
